import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root',
})
export class WalletService {
  balance: number = 0;
  balanceUpdate = new Subject<number>();
  transactions: any;
  transactionsUpdate = new Subject<any>();
  baseUrl: string = environment.apiUrl;
  walletUrl: string = `${environment.reportUrl}api/breeze/`;

  // Auth is now handled by XClientKeyInterceptor, which attaches
  // Authorization: Bearer <token> from the get-auth session token. These
  // header objects just carry the skip-error-interceptor opt-out where
  // needed.
  private xClientHeader = {
    headers: new HttpHeaders({}),
  };

  private xClientHeaderWithSkipErrorInterceptor = {
    headers: new HttpHeaders({
      'skip-error-interceptor': 'true',
    }),
  };

  constructor(
    private http: HttpClient,
    private storage: StorageService,
  ) {}

  // credit-history/?page=1&page_size=10
  getTransactions(
    pageNumber = environment.wallet.startPageageNumebr,
    pageSize = environment.wallet.itemsPerPage,
    userID = null,
  ) {
    const profile = this.storage.get('user');
    userID ? null : (userID = profile?.userID);
    const params = {
      page: pageNumber,
      page_size: pageSize,
    };
    const header: any = this.xClientHeader;
    header.params = params;

    this.http
      .get(`${this.baseUrl}wallet/${userID}/credit-history/`, header)
      .subscribe(
        (res: any) => {
          this.transactions = res;
          this.transactionsUpdate.next(this.transactions);
        },
        (err) => {
          this.transactions = { results: [] };
          this.transactionsUpdate.next(this.transactions);
        },
      );

    return this.transactionsUpdate.asObservable();
  }

  downloadTransactions(
    pageNumber = environment.wallet.startPageageNumebr,
    pageSize = environment.wallet.itemsPerPage,
    userID = null,
  ) {
    const profile = JSON.parse(this.storage.get('user') || '');
    userID ? null : (userID = profile?.userID);
    const params = {
      page: pageNumber,
      page_size: pageSize,
    };
    const header: any = this.xClientHeader;
    header.params = params;
    header.responseType = 'arraybuffer' as 'json';
    return this.http.get(`${this.baseUrl}wallet/${userID}/download/`, header);
  }

  getBalance(userID = null) {
    const profile = this.storage.get('user');
    userID ? null : (userID = profile?.userID);
    this.http
      .get(`${this.baseUrl}wallet/${userID}/balance`, this.xClientHeader)
      .subscribe(
        (res: any) => {
          this.balance = res.data.balance;
          this.balanceUpdate.next(this.balance);
        },
        () => {
          this.balance = 0;
          this.balanceUpdate.next(this.balance);
        },
      );
  }

  getBalanceUpdated() {
    return this.balanceUpdate.asObservable();
  }

  inititateTransactions(payload: any) {
    return this.http.post(
      `${this.baseUrl}wallet/`,
      payload,
      this.xClientHeader,
    );
  }

  verifyTransactions(transaction_id: any) {
    return this.http.post(
      `${this.baseUrl}wallet/flutterwave/${transaction_id}/verify/`,
      {},
      this.xClientHeader,
    );
  }

  getBarChartReport(userID = null) {
    const profile = this.storage.get('user');
    userID ? null : (userID = profile?.userID);
    return this.http.get(
      `${this.baseUrl}wallet/${userID}/stat/?page=1&page_size=12`,
      this.xClientHeader,
    );
  }

  activateWalletSession(userId: string, pin: string) {
    return this.http.post(
      `${this.walletUrl}customer/${userId}/session`,
      { pin },
      this.xClientHeaderWithSkipErrorInterceptor,
    );
  }

  deactivateWalletSession(userId: string) {
    return this.http.delete(
      `${this.walletUrl}customer/${userId}/session`,
      this.xClientHeaderWithSkipErrorInterceptor,
    );
  }

  createCustomerWallet(walletData: any): Observable<any> {
    const url = `${this.walletUrl}customer-wallet`; // Using baseUrl

    // Store the BVN in localStorage
    this.storage.setBvn(walletData.BVN);

    return this.http.post(url, walletData, this.xClientHeader);
  }

  createWallet(
    walletData: any,
    skipErrorInterceptor: boolean = false,
  ): Observable<any> {
    const url = `${this.walletUrl}wallet`; // Using baseUrl

    // Store the BVN in localStorage
    this.storage.setBvn(walletData.BVN);

    const headers = skipErrorInterceptor
      ? this.xClientHeaderWithSkipErrorInterceptor
      : this.xClientHeader;

    return this.http.post(url, walletData, headers);
  }
  setWalletPin(pinData: { user_id: string; pin: string }): Observable<any> {
    const userID = pinData.user_id;
    const url = `${this.walletUrl}customer/${userID}/set-pin`; // Endpoint for setting the wallet pin
    return this.http.post(url, { pin: pinData.pin }, this.xClientHeader);
  }

  getCardFee() {
    return this.http.get(`${this.walletUrl}card-fees`);
  }

  userCardRequest(requestData: any): Observable<any> {
    const userID = requestData.user_id;
    const url = `${this.walletUrl}customer/${userID}/card-requests`; // Adjusted endpoint for card request
    return this.http.post(
      url,
      requestData,
      this.xClientHeaderWithSkipErrorInterceptor,
    );
  }

  getCardInfo(user_id: string): Observable<any> {
    const url = `${this.walletUrl}customer/${user_id}/card`;
    return this.http.get(url, this.xClientHeaderWithSkipErrorInterceptor);
  }

  getWalletByUserId(userId: string): Observable<any> {
    const url = `${this.walletUrl}wallet/${userId}?query=breeze`;
    return this.http.get(url, this.xClientHeaderWithSkipErrorInterceptor);
  }

  activateCard(userId: string, payload: any) {
    return this.http.post(`${this.walletUrl}cards/${userId}/activate`, payload);
  }

  changePin(userId: string, data: any) {
    return this.http.post(`${this.walletUrl}cards/${userId}/change-pin`, data);
  }
}
